import React from "react";
import { ShieldCheck, XCircle, CheckCircle2, PackageOpen } from "lucide-react";
import useMediaQuery from "../../components/shared/useMediaQuery";

const DEDUCTION_SCHEDULE = [
  {
    icon: "🧧",
    title: "Elite Trainer Boxes",
    rate: "−$30",
    note: "Per damaged unit · flat, any severity",
    tint: "#f27059",
  },
  {
    icon: "📦",
    title: "Booster Bundles",
    rate: "−$10",
    note: "Per damaged unit · flat, any severity",
    tint: "#e8b84b",
  },
  {
    icon: "🎴",
    title: "Collections & Other Sealed",
    rate: "Photo-reviewed",
    note: "Deduction set case-by-case by severity, backed by inspection photos",
    tint: "#af58f2",
  },
];

const DAMAGE_CHIPS = [
  "Crushed / dinged corners",
  "Creases",
  "Panel dents",
  "Torn / punctured shrink",
  "Split seams",
  "Scuffs",
  "Sun fade",
  "Sticker residue",
];

const PACKAGING_STEPS = [
  { t: "Wrap every unit", s: "Bubble wrap each item individually — two layers, taped" },
  { t: "Double-walled box", s: "New or like-new corrugate only — no thin mailers" },
  { t: "Kraft paper walls", s: "Firm void fill, all six sides, 2\" minimum clearance" },
  { t: "Zero empty space", s: "Shake test: if anything moves, repack it" },
  { t: "Max 50 lbs per box", s: "Split heavier sell-ins — overweight boxes get crushed" },
];

function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.08em",
        color: "rgba(255,255,255,0.4)",
        marginBottom: 16,
      }}
    >
      {children}
    </div>
  );
}

function Panel({ children, style, isMobile }) {
  return (
    <div
      style={{
        borderRadius: 18,
        border: "1px solid rgba(255,255,255,0.06)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
        boxShadow: "0 16px 40px -26px rgba(0,0,0,0.6)",
        padding: isMobile ? 18 : 28,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Callout({ icon, iconColor, title, titleColor, children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        padding: "20px 24px",
        borderRadius: 14,
        borderLeft: `3px solid ${iconColor}`,
        background: `linear-gradient(90deg, ${iconColor}0f, transparent 60%)`,
      }}
    >
      <div style={{ marginRight: 16 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 800, color: titleColor || "#fff", marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 13, color: "#9aa0a6", lineHeight: 1.7 }}>{children}</div>
      </div>
    </div>
  );
}

function Section({ children, isFirst }) {
  return <div style={{ marginTop: isFirst ? 0 : 40 }}>{children}</div>;
}

export default function ConditionDamagesPage() {
  const isMobile = useMediaQuery("(max-width: 640px)");
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div>
        <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: "#fff", margin: 0, letterSpacing: "-0.01em" }}>
          Condition & Damages
        </h1>
        <p style={{ fontSize: 14, color: "#c3c8cc", margin: "8px 0 0", maxWidth: 640, lineHeight: 1.6 }}>
          We pay collector prices — collectors buy condition. Here's what that means and how to
          protect your payout.
        </p>
      </div>

      {/* Hero */}
      <Section>
        <div
          style={{
            borderRadius: 20,
            border: "1px solid rgba(0,173,181,0.14)",
            background: "radial-gradient(circle at 85% 0%, rgba(0,173,181,0.1), transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
            boxShadow: "0 20px 48px -26px rgba(0,0,0,0.65)",
            padding: isMobile ? 20 : 36,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: isMobile ? 16 : 0,
          }}
        >
          <div
            style={{
              flexShrink: 0,
              width: 76,
              height: 76,
              marginRight: isMobile ? 0 : 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              background: "rgba(0,173,181,0.1)",
            }}
          >
            <ShieldCheck size={36} color="#00ADB5" />
          </div>
          <div style={{ flex: "1 1 320px" }}>
            <h2 style={{ fontSize: 19, fontWeight: 800, color: "#fff", margin: "0 0 10px", lineHeight: 1.35 }}>
              Every unit is inspected, photographed & serialized.
            </h2>
            <p style={{ fontSize: 13.5, color: "#9aa0a6", lineHeight: 1.7, margin: 0, maxWidth: 640 }}>
              Sealed product is a collector's market — a dented box and a mint box are different
              products with different values. Our quotes assume collector-grade condition: crisp
              corners, intact seals, clean surfaces, undamaged shrink.
            </p>
          </div>
        </div>
      </Section>

      {/* Deduction schedule */}
      <Section>
        <SectionLabel>THE DEDUCTION SCHEDULE — PUBLISHED, NOT IMPROVISED</SectionLabel>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          {DEDUCTION_SCHEDULE.map((d) => (
            <div
              key={d.title}
              style={{
                flex: "1 1 240px",
                borderRadius: 18,
                border: "1px solid rgba(255,255,255,0.06)",
                background: `linear-gradient(160deg, ${d.tint}14, transparent 55%)`,
                boxShadow: "0 16px 36px -24px rgba(0,0,0,0.6)",
                padding: isMobile ? 18 : 24,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <span style={{ fontSize: 26 }}>{d.icon}</span>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginTop: 12 }}>{d.title}</div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: d.tint,
                  marginTop: 8,
                  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                }}
              >
                {d.rate}
              </div>
              <div style={{ fontSize: 12.5, color: "#9aa0a6", lineHeight: 1.5, marginTop: 8 }}>{d.note}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Loose packs rejected */}
      <Section>
        <Callout
          icon={<XCircle size={26} color="#f27059" style={{ flexShrink: 0, marginTop: 2 }} />}
          iconColor="#f27059"
          title="Loose Packs — damaged = rejected"
        >
          A pack's value lives in its seal. Crushed, creased, torn or tampered packs cannot be sold
          to collectors at any price — so there is no deduction tier for packs. A damaged pack is
          removed from your payout entirely. Rejected items are documented and held; request return
          within 7 days of your payout statement (return shipping at your cost).
        </Callout>
      </Section>

      {/* What counts as damage */}
      <Section>
        <SectionLabel>WHAT COUNTS AS DAMAGE</SectionLabel>
        <Panel isMobile={isMobile}>
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {DAMAGE_CHIPS.map((chip) => (
              <span
                key={chip}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "8px 14px",
                  borderRadius: 9999,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#f27059",
                  border: "1px solid rgba(242,112,89,0.22)",
                  background: "rgba(242,112,89,0.05)",
                  marginRight: 10,
                  marginBottom: 10,
                }}
              >
                ✕ {chip}
              </span>
            ))}
          </div>
        </Panel>
      </Section>

      {/* Packaging spec */}
      <Section>
        <SectionLabel>THE PACKAGING SPEC — 5 RULES, ZERO EXCEPTIONS</SectionLabel>
        <Panel isMobile={isMobile}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {PACKAGING_STEPS.map((step, i) => (
              <div
                key={step.t}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginTop: i === 0 ? 0 : 22,
                }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    marginRight: 16,
                    borderRadius: "50%",
                    background: "rgba(0,173,181,0.12)",
                    color: "#00ADB5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 800,
                  }}
                >
                  {i + 1}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{step.t}</div>
                  <div style={{ fontSize: 12.5, color: "#9aa0a6", marginTop: 3 }}>{step.s}</div>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 24,
              padding: "14px 18px",
              borderRadius: 12,
              background: "rgba(0,173,181,0.05)",
              border: "1px solid rgba(0,173,181,0.12)",
              fontSize: 12.5,
              color: "#9aa0a6",
              display: "flex",
              alignItems: "center",
            }}
          >
            <PackageOpen size={17} style={{ flexShrink: 0, marginRight: 10, color: "#00ADB5" }} />
            Damage traced to inadequate packaging is deducted per the schedule — and no carrier
            claim can be filed on badly packed freight.
          </div>
        </Panel>
      </Section>

      {/* When it isn't your fault */}
      <Section>
        <SectionLabel>WHEN IT ISN'T YOUR FAULT, WE ACT LIKE IT</SectionLabel>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          <div style={{ flex: "1 1 300px" }}>
            <Callout
              icon={<span style={{ fontSize: 18, color: "#4caf50", flexShrink: 0 }}>✓</span>}
              iconColor="#4caf50"
              titleColor="#4caf50"
              title="Carrier claim — one scenario"
            >
              We file a carrier claim only when the outer shipping box itself arrives visibly
              carrier-damaged (crushed, punctured, forklift) — documented in our arrival photos —
              and your packing met the spec.
            </Callout>
          </div>
          <div style={{ flex: "1 1 300px" }}>
            <Callout
              icon={<span style={{ fontSize: 18, color: "#e8b84b", flexShrink: 0 }}>◆</span>}
              iconColor="#e8b84b"
              titleColor="#e8b84b"
              title="Factory defects"
            >
              Factory assembly dents and factory wrap creases are reviewed case-by-case with
              photos. Tip: photograph factory flaws before shipping and note them in your sell-in.
            </Callout>
          </div>
        </div>
      </Section>

      {/* Options */}
      <Section>
        <SectionLabel>YOUR OPTIONS ON ANY DEDUCTION</SectionLabel>
        <Panel isMobile={isMobile}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <div style={{ flex: "1 1 240px", display: "flex" }}>
              <CheckCircle2 size={22} color="#4caf50" style={{ flexShrink: 0, marginTop: 1, marginRight: 12 }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>
                  Accept the deducted payout
                </div>
                <div style={{ fontSize: 12.5, color: "#9aa0a6", marginTop: 3 }}>Itemized on your statement</div>
              </div>
            </div>
            <div style={{ flex: "1 1 240px", display: "flex" }}>
              <PackageOpen size={22} color="#00ADB5" style={{ flexShrink: 0, marginTop: 1, marginRight: 12 }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>
                  Have the unit returned
                </div>
                <div style={{ fontSize: 12.5, color: "#9aa0a6", marginTop: 3 }}>
                  Return shipping at your cost · within 7 days
                </div>
              </div>
            </div>
          </div>
          <p style={{ fontSize: 12.5, color: "#9aa0a6", marginTop: 24, marginBottom: 0, lineHeight: 1.6 }}>
            Inspection photos are available on request — open a ticket with your shipment number.
            Appeals within 7 days of your payout statement. After 7 days, statements are final.
          </p>
        </Panel>
      </Section>

      <p
        style={{
          textAlign: "center",
          letterSpacing: "0.12em",
          fontSize: 11,
          color: "#5c6166",
          margin: "40px 0 0",
        }}
      >
        EVERY UNIT INSPECTED · EVERY UNIT PHOTOGRAPHED · EVERY UNIT SERIALIZED
      </p>
    </div>
  );
}
